import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useRouter } from "next/router";
import type { FC } from "react";
import { useEffect, useState } from "react";

import type { Dayjs } from "@calcom/dayjs";
import dayjs from "@calcom/dayjs";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import type { TimeFormat } from "@calcom/lib/timeFormat";
import { nameOfDay } from "@calcom/lib/weekday";
import type { Slot } from "@calcom/trpc/server/routers/viewer/slots";
import { SkeletonButton, SkeletonText } from "@calcom/ui";

import classNames from "@lib/classNames";
import { timeZone } from "@lib/clock";

type NoaAvailableTimesProps = {
  timeFormat: TimeFormat;
  onTimeFormatChange: (is24Hour: boolean) => void;
  eventTypeId: number;
  recurringCount: number | undefined;
  eventTypeSlug: string;
  date?: Dayjs;
  seatsPerTimeSlot?: number | null;
  slots?: Slot[];
  isLoading: boolean;
  ethSignature?: string;
  onSelectSlot: (slot: Slot) => void;
};

const NoaAvailableTimes: FC<NoaAvailableTimesProps> = ({
  slots = [],
  isLoading,
  date,
  eventTypeId,
  eventTypeSlug,
  recurringCount,
  timeFormat,
  onTimeFormatChange,
  seatsPerTimeSlot,
  ethSignature,
  onSelectSlot,
}) => {
  const [slotPickerRef] = useAutoAnimate<HTMLDivElement>();
  const { t, i18n } = useLocale();
  const router = useRouter();
  const { rescheduleUid } = router.query;

  const [brand, setBrand] = useState("#292929");

  useEffect(() => {
    setBrand(getComputedStyle(document.documentElement).getPropertyValue("--brand-color").trim());
  }, []);

  return (
    <div ref={slotPickerRef}>
      {!!date ? (
        <div className="flex h-full w-full flex-col text-center md:-mb-5 md:min-w-[200px]">
          <div className="text-brand-500 box-border flex min-h-[68px] items-center justify-between border-b border-gray-300 p-2 pl-5 text-lg">
            <div className="mr-4">{`${nameOfDay(i18n.language, Number(date.format("d")), "long")}, ${date
              .toDate()
              .toLocaleString(i18n.language, { month: "long", day: "numeric" })}`}</div>
            {/* <div className="ml-auto">
              <ToggleGroup
                onValueChange={(timeFormat) => onTimeFormatChange(timeFormat === "24")}
                defaultValue={timeFormat === TimeFormat.TWELVE_HOUR ? "12" : "24"}
                options={[
                  { value: "12", label: t("12_hour_short") },
                  { value: "24", label: t("24_hour_short") },
                ]}
              />
            </div> */}
          </div>
          <div className="flex flex-grow flex-col gap-3 p-5 md:h-[364px] md:overflow-y-auto">
            {slots.length > 0 &&
              slots.map((slot) => {
                type BookingURL = {
                  pathname: string;
                  query: Record<string, string | number | string[] | undefined>;
                };
                const bookingUrl: BookingURL = {
                  // TODO: This is a hack to get the correct path for the booking page
                  pathname: `/team/noa-ignite/book`,
                  query: {
                    ...router.query,
                    date: dayjs.utc(slot.time).tz(timeZone()).format(),
                    type: eventTypeId,
                    slug: eventTypeSlug,
                    /** Treat as recurring only when a count exist and it's not a rescheduling workflow */
                    count: recurringCount && !rescheduleUid ? recurringCount : undefined,
                    ...(ethSignature ? { ethSignature } : {}),
                  },
                };

                if (rescheduleUid) {
                  bookingUrl.query.rescheduleUid = rescheduleUid as string;
                }

                // If event already has an attendee add booking id
                if (slot.bookingUid) {
                  bookingUrl.query.bookingUid = slot.bookingUid;
                }

                return (
                  <div data-slot-owner={(slot.userIds || []).join(",")} key={`${dayjs(slot.time).format()}`}>
                    {/* ^ data-slot-owner is helpful in debugging and used to identify the owners of the slot. Owners are the users which have the timeslot in their schedule. It doesn't consider if a user has that timeslot booked */}
                    {/* Current there is no way to disable Next.js Links */}
                    {seatsPerTimeSlot && slot.attendees && slot.attendees >= seatsPerTimeSlot ? (
                      <div
                        className={classNames(
                          "text-primary-500 block border bg-white py-2 font-medium opacity-25 dark:border-transparent ",
                          brand === "#fff" || brand === "#ffffff" ? "" : ""
                        )}>
                        {dayjs(slot.time).tz(timeZone()).format(timeFormat)}
                        {!!seatsPerTimeSlot && <p className="text-sm">{t("booking_full")}</p>}
                      </div>
                    ) : (
                      <button
                        // href={bookingUrl}
                        // prefetch={false}
                        className="hover:bg-brand-500 border-brand-500 block w-full border bg-white py-2 text-lg hover:text-white"
                        onClick={() => onSelectSlot(slot)}
                        data-testid="time">
                        {dayjs(slot.time).tz(timeZone()).format(timeFormat)}
                        {!!seatsPerTimeSlot && (
                          <p
                            className={`${
                              slot.attendees && slot.attendees / seatsPerTimeSlot >= 0.8
                                ? "text-rose-600"
                                : slot.attendees && slot.attendees / seatsPerTimeSlot >= 0.33
                                ? "text-yellow-500"
                                : "text-emerald-400"
                            } text-sm`}>
                            {slot.attendees ? seatsPerTimeSlot - slot.attendees : seatsPerTimeSlot} /{" "}
                            {seatsPerTimeSlot} {t("seats_available")}
                          </p>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}

            {!isLoading && !slots.length && (
              <div className="-mt-4 flex h-full w-full flex-col content-center items-center justify-center">
                <h1 className="my-6 text-xl text-black dark:text-white">{t("all_booked_today")}</h1>
              </div>
            )}

            {isLoading && !slots.length && (
              <>
                <SkeletonButton className="h-[46px] w-full rounded-none" />
                <SkeletonButton className="h-[46px] w-full rounded-none" />
                <SkeletonButton className="h-[46px] w-full rounded-none" />
                <SkeletonButton className="h-[46px] w-full rounded-none" />
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex h-full w-full flex-col text-center md:-mb-5 md:min-w-[200px]">
          <div className="text-brand-500 box-border flex min-h-[68px] items-center justify-between border-b border-gray-300 p-2 pl-5 text-lg">
            <SkeletonText className="mr-4 h-4 w-24" />
          </div>
          <div className="flex flex-grow flex-col gap-3 p-5">
            <SkeletonButton className="h-[46px] w-full rounded-none" />
            <SkeletonButton className="h-[46px] w-full rounded-none" />
            <SkeletonButton className="h-[46px] w-full rounded-none" />
            <SkeletonButton className="h-[46px] w-full rounded-none" />
          </div>
        </div>
      )}
    </div>
  );
};

NoaAvailableTimes.displayName = "NoaAvailableTimes";

export default NoaAvailableTimes;
